import React, { useState, useEffect, useCallback } from "react";
import { Award, Heart, Users, BadgeCheck, Baby, X, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "@/App";

const ABOUT_IMG =
  "https://customer-assets.emergentagent.com/job_audicion-amplisoon/artifacts/mbvggmfp_violeta.JPG";

const STAT_ICONS = [Award, Heart, Users];

const GALLERY_IMAGES = [
  "https://customer-assets-0z36b82j.emergentagent.net/job_audicion-amplisoon/artifacts/k4qmkc1c_sala%20de%20espera%201.jpeg",
  "https://customer-assets-0z36b82j.emergentagent.net/job_audicion-amplisoon/artifacts/xy7gav26_sala%20de%20espera%202.jpeg",
  "https://customer-assets-0z36b82j.emergentagent.net/job_audicion-amplisoon/artifacts/8cjq7yvd_recepci%C3%B3n.jpeg",
  "https://customer-assets-0z36b82j.emergentagent.net/job_audicion-amplisoon/artifacts/tefhllf5_Gabinete%201.jpeg",
  "https://customer-assets-0z36b82j.emergentagent.net/job_audicion-amplisoon/artifacts/stu24hs3_Gabinete%202%20%28infantil%29.jpeg",
  "https://customer-assets-0z36b82j.emergentagent.net/job_audicion-amplisoon/artifacts/5i6nwv7o_Gabinete%202.jpeg",
];

export default function About() {
  const { t } = useLang();
  const [lightbox, setLightbox] = useState(null);

  const close = useCallback(() => setLightbox(null), []);
  const prev = useCallback(
    () => setLightbox((i) => (i - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length),
    []
  );
  const next = useCallback(
    () => setLightbox((i) => (i + 1) % GALLERY_IMAGES.length),
    []
  );

  useEffect(() => {
    if (lightbox === null) return;
    const onKey = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [lightbox, close, prev, next]);

  return (
    <section id="sobre" className="py-20 md:py-28 bg-white" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        <div className="lg:col-span-5 order-2 lg:order-1">
          <div className="relative">
            <div className="absolute -inset-3 bg-gradient-to-br from-sky-200/50 to-blue-300/30 rounded-3xl blur-xl" />
            <div className="relative rounded-3xl overflow-hidden shadow-xl border border-slate-200">
              <img
                src={ABOUT_IMG}
                alt="Violeta Jacobo, audióloga responsable de Amplisoon"
                className="w-full h-[520px] object-cover"
                data-testid="about-image"
              />
            </div>
            <div className="absolute -bottom-5 -right-5 bg-white rounded-2xl shadow-xl border border-slate-100 p-5">
              <div className="text-3xl font-extrabold text-blue-900">Violeta</div>
              <div className="text-sm text-slate-600 uppercase tracking-widest font-semibold">
                Audióloga
              </div>
            </div>
          </div>
        </div>
        <div className="lg:col-span-7 order-1 lg:order-2">
          <p className="text-sm font-bold uppercase tracking-widest text-sky-700">
            {t.about.kicker}
          </p>
          <h2 className="mt-3 text-4xl md:text-5xl font-extrabold tracking-tight text-blue-950 leading-[1.1]">
            {t.about.title}
          </h2>
          <p className="mt-6 text-lg text-slate-700 leading-relaxed">{t.about.p1}</p>
          <p className="mt-4 text-lg text-slate-700 leading-relaxed">{t.about.p2}</p>
          <p className="mt-4 text-lg text-slate-700 leading-relaxed">{t.about.p3}</p>

          <div className="mt-6 flex flex-wrap gap-3">
            <span
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-900 text-white text-sm font-semibold"
              data-testid="about-cep-badge"
            >
              <BadgeCheck className="w-4 h-4" />
              {t.about.cepBadge}
            </span>
            <span
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-900 text-white text-sm font-semibold"
              data-testid="about-philips-badge"
            >
              <BadgeCheck className="w-4 h-4" />
              {t.about.philipsBadge}
            </span>
            <span
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sky-100 text-sky-900 text-sm font-semibold"
              data-testid="about-kids-badge"
            >
              <Baby className="w-4 h-4" />
              {t.about.kidsBadge}
            </span>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-4">
            {t.about.stats.map((s, i) => {
              const Icon = STAT_ICONS[i] || Award;
              return (
                <div
                  key={i}
                  className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-center"
                  data-testid={`about-stat-${i}`}
                >
                  <Icon className="w-6 h-6 mx-auto text-sky-700" />
                  <div className="mt-3 text-3xl font-extrabold text-blue-900">{s.value}</div>
                  <div className="text-sm text-slate-600 font-medium leading-tight mt-1">
                    {s.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Centre gallery */}
      <div className="max-w-7xl mx-auto px-5 md:px-8 lg:px-12 mt-20" data-testid="about-gallery">
        <div className="text-center max-w-3xl mx-auto">
          <h3 className="text-3xl md:text-4xl font-extrabold tracking-tight text-blue-950">
            {t.about.galleryTitle}
          </h3>
          <p className="mt-3 text-lg text-slate-600">{t.about.gallerySubtitle}</p>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {GALLERY_IMAGES.map((src, i) => (
            <figure
              key={i}
              onClick={() => setLightbox(i)}
              className={`group relative rounded-2xl overflow-hidden border border-slate-200 shadow-md cursor-zoom-in ${
                i === 0 || i === 3 || i === 4 ? "lg:col-span-2" : ""
              }`}
              data-testid={`gallery-item-${i}`}
            >
              <img
                src={src}
                alt={`${t.about.galleryCaptions[i]} — Amplisoon El Campello`}
                loading="lazy"
                className="w-full h-64 md:h-72 object-cover transition-transform duration-500 group-hover:scale-105"
              />
              <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-blue-950/90 via-blue-950/50 to-transparent px-5 pt-10 pb-4">
                <span className="text-white font-bold text-base md:text-lg drop-shadow">
                  {t.about.galleryCaptions[i]}
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox !== null && (
        <div
          className="fixed inset-0 z-[100] bg-blue-950/95 backdrop-blur-sm flex flex-col items-center justify-center p-4"
          onClick={close}
          data-testid="gallery-lightbox"
        >
          <button
            onClick={close}
            className="absolute top-4 right-4 md:top-6 md:right-6 grid place-items-center w-11 h-11 rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors"
            aria-label="Cerrar"
            data-testid="lightbox-close"
          >
            <X className="w-6 h-6" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); prev(); }}
            className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 grid place-items-center w-11 h-11 md:w-14 md:h-14 rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors"
            aria-label="Anterior"
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="w-7 h-7" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); next(); }}
            className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 grid place-items-center w-11 h-11 md:w-14 md:h-14 rounded-full bg-white/10 hover:bg-white/25 text-white transition-colors"
            aria-label="Siguiente"
            data-testid="lightbox-next"
          >
            <ChevronRight className="w-7 h-7" />
          </button>
          <img
            src={GALLERY_IMAGES[lightbox]}
            alt={t.about.galleryCaptions[lightbox]}
            onClick={(e) => e.stopPropagation()}
            className="max-w-[92vw] max-h-[80vh] object-contain rounded-xl shadow-2xl"
            data-testid="lightbox-image"
          />
          <div className="mt-4 text-white text-lg md:text-xl font-bold" data-testid="lightbox-caption">
            {t.about.galleryCaptions[lightbox]}
            <span className="ml-3 text-sm font-medium text-sky-200">
              {lightbox + 1} / {GALLERY_IMAGES.length}
            </span>
          </div>
        </div>
      )}
    </section>
  );
}
